import { useFormContext } from 'react-hook-form';
import {
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { SelectPlaceholder } from '~/components';
import {
  clusterIopsRules,
  clusterNameRules,
  clusterSizeRules,
  clusterThroughputRules,
} from '~/validation/clusterSchema';
import { clusterFormContainerStyles, labelStyles } from './styles';

enum InstanceEnum {
  M5_XLARGE = 'm5.xlarge',
  C5_XLARGE = 'c5.xlarge',
  G5_XLARGE = 'g5.xlarge',
}

const CreateEmrClusterForm = () => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  return (
    <Grid container spacing={4} rowSpacing={{ sm: 4, xs: 6 }} sx={clusterFormContainerStyles}>
      <Grid size={12}>
        <FormControlLabel
          required
          label="CLUSTER NAME"
          control={
            <TextField
              {...register('name')}
              error={!!errors.name}
              helperText={errors.name?.message as string}
              placeholder="Enter the Cluster Name"
              slotProps={{ htmlInput: { maxLength: clusterNameRules.max } }}
            />
          }
        />
      </Grid>
      <Grid size={6}>
        <FormControlLabel
          required
          label="PRIMARY EC2 INSTANCE TYPE"
          control={
            <>
              <FormHelperText error={!!errors.primary_ec2_instance_type}>
                {errors.primary_ec2_instance_type?.message as string}
              </FormHelperText>
              <Select
                {...register('primary_ec2_instance_type')}
                renderValue={(selected: InstanceEnum) => {
                  if (!selected?.length) return <SelectPlaceholder>Select Primary EC2 Instance Type</SelectPlaceholder>;

                  return selected;
                }}
              >
                {Object.values(InstanceEnum).map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </>
          }
        />
      </Grid>
      <Grid size={6}>
        <FormControlLabel
          required
          label="CORE EC2 INSTANCE TYPE"
          control={
            <>
              <FormHelperText error={!!errors.core_ec2_instance_type}>
                {errors.core_ec2_instance_type?.message as string}
              </FormHelperText>
              <Select
                {...register('core_ec2_instance_type')}
                error={!!errors.core_ec2_instance_type}
                renderValue={(selected: InstanceEnum) => {
                  if (!selected?.length) return <SelectPlaceholder>Select Core EC2 Instance Type</SelectPlaceholder>;

                  return selected;
                }}
              >
                {Object.values(InstanceEnum).map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </>
          }
        />
      </Grid>
      <Grid size={12}>
        <Typography sx={labelStyles}>
          *EBS ROOT VOLUME
          <Typography component="div" variant="caption" color="#0000008C">
            EBS root volume applies to the operating systems and applications that you install on the cluster.{' '}
            <a target="_blank" rel="noopener noreferrer" href="https://aws.amazon.com/ebs/volume-types/">
              EBS root volume ratio constraints
            </a>
          </Typography>
        </Typography>
      </Grid>
      <Grid size={{ sm: 4, xs: 12 }}>
        <FormControlLabel
          required
          label="SIZE (GiB)"
          control={
            <TextField
              {...register('ebs_size')}
              placeholder="Enter Size"
              type="number"
              error={!!errors.ebs_size}
              helperText={
                (errors.ebs_size?.message as string) || (
                  <span>
                    {`${clusterSizeRules.min}-${clusterSizeRules.max} GiB per volume`}
                    <br />
                    Choose a maximum ratio of 50:1 between volume size and number of vCPUs in the cluster.
                  </span>
                )
              }
            />
          }
        />
      </Grid>
      <Grid size={{ sm: 4, xs: 12 }}>
        <FormControlLabel
          required
          label="IOPS"
          control={
            <TextField
              {...register('ebs_iops')}
              placeholder="Enter IOPS"
              error={!!errors.ebs_iops}
              type="number"
              helperText={
                (errors.ebs_iops?.message as string) || (
                  <span>
                    {`${clusterIopsRules.min}-${clusterIopsRules.max} IOPS per volume`}
                    <br />
                    Choose a maximum ratio of 500:1 between IOPS and volume size.
                  </span>
                )
              }
            />
          }
        />
      </Grid>
      <Grid size={{ sm: 4, xs: 12 }}>
        <FormControlLabel
          required
          label="THROUGHPUT (MiB/s)"
          control={
            <TextField
              {...register('ebs_throughput')}
              placeholder="Enter Throughput"
              type="number"
              error={!!errors.ebs_throughput}
              helperText={
                (errors.ebs_throughput?.message as string) || (
                  <span>
                    {`${clusterThroughputRules.min}-${clusterThroughputRules.max} MiB/s per volume`}
                    <br />
                    Choose a maximum ratio of 0.25:1 between throughput and IOPS.
                  </span>
                )
              }
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">MiB/s</InputAdornment>,
                },
              }}
            />
          }
        />
      </Grid>
    </Grid>
  );
};

export default CreateEmrClusterForm;
